import { useState } from 'react';
import { Button, Calendar, Popover } from '@strivelabs/ui-core/v2';
import { ArrowRight, Calendar as CalendarIcon, X } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { formatTaskDate, toLocalDate, toStoredDay } from '../taskDates';
import { INLINE_FIELD_BOX } from './TaskInlinePicker';

interface DateFieldProps {
  value: string | null;
  label: string;
  placeholder: string;
  min?: Date;
  max?: Date;
  disabled?: boolean;
  /** Classes for the Popover's wrapper, which carries any bleed (see INLINE_FIELD_BLEED). */
  triggerClassName?: string;
  onChange: (value: string | null) => void;
}

const DateField = ({
  value, label, placeholder, min, max, disabled = false, triggerClassName, onChange,
}: DateFieldProps) => {
  const [ open, setOpen ] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      align="start"
      triggerClassName={triggerClassName}
      trigger={
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          className={cn(INLINE_FIELD_BOX, 'text-[13.5px]', disabled && 'pointer-events-none')}
        >
          {/* The glyph follows its own value: the mock's calendar carries the
              row's ink, and a placeholder that has nothing to say stays grey
              along with its words. */}
          <CalendarIcon
            className={cn('size-4 shrink-0', value ? 'text-[#181818]' : 'text-[#9A9A9A]')}
            aria-hidden
          />
          <span className={value ? 'text-[#181818]' : 'text-[#9A9A9A]'}>
            {formatTaskDate(value) ?? placeholder}
          </span>
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        <Calendar
          mode="single"
          selected={toLocalDate(value)}
          min={min}
          max={max}
          onSelect={(date) => {
            onChange(date ? toStoredDay(date) : null);
            setOpen(false);
          }}
        />
        {value ? (
          <Button
            variant="borderless"
            size="xs"
            icon={<X aria-hidden />}
            className="self-start"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
    </Popover>
  );
};

interface TaskDatesRowProps {
  startDate: string | null;
  dueDate: string | null;
  disabled?: boolean;
  onChange: (dates: { start_date?: string | null; due_date?: string | null }) => void;
}

/**
 * The task's start and due dates, as one span.
 *
 * Each calendar bounds the other so a due date cannot be pulled before the
 * start. The server accepts either order, so this is the only place the rule
 * holds — a task the agent dated the other way round still renders.
 *
 * Only the start bleeds into the gutter, so its text lines up with the other
 * rows. The sides facing the arrow keep their padding in flow: bled there too,
 * each hover fill ran under the arrow and the neighbouring field's padding.
 */
export const TaskDatesRow = ({
  startDate, dueDate, disabled = false, onChange,
}: TaskDatesRowProps) => (
  <div className="flex min-w-0 items-center gap-1">
    <DateField
      value={startDate}
      label="Start date"
      placeholder="Add start date"
      max={toLocalDate(dueDate)}
      disabled={disabled}
      triggerClassName="-ml-[9px]"
      onChange={(value) => onChange({ start_date: value })}
    />
    <ArrowRight className="size-[15px] shrink-0 text-[#9A9A9A]" aria-hidden />
    <DateField
      value={dueDate}
      label="Due date"
      placeholder="Add due date"
      min={toLocalDate(startDate)}
      disabled={disabled}
      onChange={(value) => onChange({ due_date: value })}
    />
  </div>
);
